import type { RuleKey, RuleConfig, EntityData, ScoredEntity, RiskClass } from './safety-types';

// ── Type 1: Distance-Based (hardAccel, harshBraking, harshCornering, idling) ─

export function scoreType1(eventCount: number, totalDistKm: number): number {
  if (totalDistKm <= 0) return 0;
  return Math.max(0, 100 - (eventCount * 1609.34) / totalDistKm);
}

// ── Type 2: Tiered Penalty (speeding) ─────────────────────────────────────

export function scoreTiered(pct: number): number {
  if (pct <= 0)   return 100;
  if (pct < 0.3)  return 100 - pct * 6;
  if (pct < 0.7)  return 100 - pct * 7;
  if (pct < 1.0)  return 100 - pct * 8;
  if (pct < 1.3)  return 100 - pct * 9;
  if (pct < 1.7)  return 100 - pct * 10;
  if (pct < 2.0)  return 100 - pct * 11;
  if (pct < 2.3)  return 100 - pct * 12;
  if (pct < 2.7)  return 100 - pct * 13;
  if (pct < 3.0)  return 100 - pct * 14;
  if (pct < 3.3)  return 100 - pct * 15;
  if (pct < 3.7)  return 100 - pct * 16;
  if (pct < 4.0)  return 100 - pct * 17;
  if (pct < 4.3)  return 100 - pct * 18;
  if (pct < 4.7)  return 100 - pct * 19;
  if (pct <= 5.0) return 100 - pct * 20;
  return 0;
}

// ── Type 3: Hybrid Seat Belt ───────────────────────────────────────────────

export function scoreSeatBelt(
  eventCount:      number,
  exceptionDistKm: number,
  totalDistKm:     number,
): number {
  if (totalDistKm <= 0) return 0;
  const pct          = (exceptionDistKm / totalDistKm) * 100;
  const percentScore = scoreTiered(pct);
  const countScore   = scoreType1(eventCount, totalDistKm);
  return 0.7 * percentScore + 0.3 * countScore;
}

// ── Per-rule dispatcher ────────────────────────────────────────────────────

export function scoreRule(
  key:             RuleKey,
  eventCount:      number,
  exceptionDistKm: number,
  totalDistKm:     number,
): number {
  if (key === 'speeding') {
    if (totalDistKm <= 0) return 0;
    const pct = (exceptionDistKm / totalDistKm) * 100;
    return scoreTiered(pct);
  }
  if (key === 'seatBelt') {
    return scoreSeatBelt(eventCount, exceptionDistKm, totalDistKm);
  }
  // hardAccel, harshBraking, harshCornering, idling — all Type 1
  return scoreType1(eventCount, totalDistKm);
}

// ── Risk classification ────────────────────────────────────────────────────

export function riskClass(score: number | null, totalDistKm: number): RiskClass {
  if (totalDistKm <= 0) return 'noActivity';
  if (score === null)    return 'noActivity';
  if (score >= 90) return 'low';
  if (score >= 80) return 'mild';
  if (score >= 70) return 'medium';
  return 'high';
}

export const RISK_LABELS: Record<RiskClass, string> = {
  low:        'Low Risk',
  mild:       'Mild Risk',
  medium:     'Medium Risk',
  high:       'High Risk',
  noActivity: 'No Activity',
};

export const RISK_COLORS: Record<RiskClass, string> = {
  low:        '#4CAF50',
  mild:       '#FFC107',
  medium:     '#FF9800',
  high:       '#F44336',
  noActivity: '#9E9E9E',
};

// ── Main scoring function ──────────────────────────────────────────────────

export function calculateScores(
  rawData:     Map<string, EntityData>,
  ruleConfigs: RuleConfig[],
): ScoredEntity[] {
  const enabled    = ruleConfigs.filter(r => r.enabled);
  const totalWeight = enabled.reduce((s, r) => s + r.weight, 0) || 100;

  const results: ScoredEntity[] = [];

  rawData.forEach(entity => {
    const ruleScores: Partial<Record<RuleKey, number>> = {};
    let weightedSum = 0;

    for (const rule of enabled) {
      const data  = entity.byRule[rule.key];
      const s     = scoreRule(rule.key, data.eventCount, data.exceptionDistKm, entity.totalDistanceKm);
      const score = Math.round(s * 10) / 10;
      ruleScores[rule.key] = score;
      weightedSum += score * (rule.weight / totalWeight);
    }

    const totalScore = entity.totalDistanceKm <= 0
      ? null
      : Math.round(weightedSum * 10) / 10;

    results.push({
      ...entity,
      ruleScores,
      totalScore,
      riskClass: riskClass(totalScore, entity.totalDistanceKm),
    });
  });

  // Sort: scored ascending (worst first), no-activity last, alpha tiebreak
  return results.sort((a, b) => {
    const aNA = a.riskClass === 'noActivity';
    const bNA = b.riskClass === 'noActivity';
    if (aNA !== bNA) return aNA ? 1 : -1;
    if (a.totalScore === null && b.totalScore === null) return a.name.localeCompare(b.name);
    if (a.totalScore === null) return 1;
    if (b.totalScore === null) return -1;
    return a.totalScore - b.totalScore;
  });
}
